import { ComponentPropsWithoutRef, useId } from 'react';
import { ChevronRight } from 'lucide-react';
import { Accordion as RadixAccordion } from 'radix-ui';

import { cn } from '@components/primitives/utilities';

import css from './Accordion.module.css';

export type AccordionProps =
  | (Omit<RadixAccordion.AccordionMultipleProps, 'type'> & {
      type?: 'multiple';
    })
  | RadixAccordion.AccordionSingleProps;

export type AccordionItemProps = Omit<
  RadixAccordion.AccordionItemProps,
  'value'
> & { value?: string };

export type AccordionHeaderProps = RadixAccordion.AccordionHeaderProps;
export type AccordionTriggerProps = RadixAccordion.AccordionTriggerProps;
export type AccordionContentProps = RadixAccordion.AccordionContentProps;
export type AccordionIndicatorProps = ComponentPropsWithoutRef<'div'>;

function AccordionRoot({ type, ...props }: AccordionProps) {
  const rootProps = {
    ...props,
    type: type ?? 'multiple',
  } as Parameters<typeof RadixAccordion.Root>[0];

  return <RadixAccordion.Root {...rootProps} />;
}

function AccordionItem({ value, ...props }: AccordionItemProps) {
  const id = useId();

  return <RadixAccordion.Item value={value ?? id} {...props} />;
}

/**
 * With `asChild` the caller owns the element and its styling, so our classes
 * stay out and cannot compete with an animation the caller declares.
 */
function AccordionHeader({
  asChild,
  children,
  ...props
}: AccordionHeaderProps) {
  return (
    <RadixAccordion.Header asChild {...props}>
      {asChild ? children : <div className={css.header}>{children}</div>}
    </RadixAccordion.Header>
  );
}

function AccordionContent({
  asChild,
  className,
  ...props
}: AccordionContentProps) {
  return (
    <RadixAccordion.Content
      asChild={asChild}
      className={cn(!asChild && css.content, className)}
      {...props}
    />
  );
}

function AccordionIndicator({
  className,
  children,
  ...props
}: AccordionIndicatorProps) {
  return (
    <div
      data-testid="indicator"
      className={cn(css.indicator, className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </div>
  );
}

function AccordionTrigger({
  asChild,
  children,
  ...props
}: AccordionTriggerProps) {
  return (
    <RadixAccordion.Trigger asChild {...props}>
      {asChild ? (
        children
      ) : (
        <div className={css.trigger}>
          <AccordionIndicator />
          <div className={css.triggerContent}>{children}</div>
        </div>
      )}
    </RadixAccordion.Trigger>
  );
}

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Content: AccordionContent,
  Trigger: AccordionTrigger,
  Indicator: AccordionIndicator,
});
